import { McProcessingStatus, McProfileSource, McUnifiedProfileQueryParams } from '@components/templates/employer/applicant-list/flow-mc/types';

const PROFILE_SOURCES = new Set<McProfileSource>([
  'applicant',
  'matching_candidate',
  'recommended_candidate',
  'saved_profile',
]);

const PROCESSING_STATUSES = new Set<McProcessingStatus>([
  'PROCESSING',
  'PROCESSING_FAILED',
  'INTERVIEW_PROCESSING',
  'INTERVIEW_COMPLETED',
  'INTERVIEW_FAILED',
]);

const getFirstValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const [firstValue] = value as unknown[];
    return firstValue;
  }

  return value;
};

const parseNumber = (value: unknown): number | undefined => {
  const firstValue = getFirstValue(value);
  if (typeof firstValue === 'number' && Number.isFinite(firstValue)) return firstValue;
  if (typeof firstValue === 'string' && firstValue.trim() !== '') {
    const parsedValue = Number(firstValue);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
};

const parseString = (value: unknown): string | undefined => {
  const firstValue = getFirstValue(value);
  return typeof firstValue === 'string' && firstValue.trim() !== '' ? firstValue : undefined;
};

const parseBoolean = (value: unknown): boolean | undefined => {
  const firstValue = getFirstValue(value);
  if (typeof firstValue === 'boolean') return firstValue;
  if (typeof firstValue !== 'string') return undefined;

  if (firstValue === 'true') return true;
  if (firstValue === 'false') return false;

  return undefined;
};

interface ExtractMcUnifiedProfileQueryParamsOptions {
  includeStatus?: boolean;
  includePagination?: boolean;
}

export const extractMcUnifiedProfileQueryParams = (
  params?: Record<string, unknown> | BjSearchParams,
  options: ExtractMcUnifiedProfileQueryParamsOptions = {},
): McUnifiedProfileQueryParams => {
  const {
    includeStatus = true,
    includePagination = false,
  } = options;

  if (!params) return {};

  const queryParams: McUnifiedProfileQueryParams = {};
  const profileSource = parseString(params.profileSource);
  const processingStatus = parseString(params.processingStatus);
  const minReferenceDate = parseString(params.minReferenceDate);
  const minFinalScore = parseNumber(params.minFinalScore);
  const status = parseNumber(params.status);
  const statuses = parseString(params.statuses);
  const page = parseNumber(params.page);
  const perPage = parseNumber(params.perPage);
  const offset = parseNumber(params.offset);
  const withPlaceholders = parseBoolean(params.withPlaceholders);

  if (profileSource && PROFILE_SOURCES.has(profileSource as McProfileSource)) {
    queryParams.profileSource = profileSource as McProfileSource;
  }

  if (processingStatus && PROCESSING_STATUSES.has(processingStatus as McProcessingStatus)) {
    queryParams.processingStatus = processingStatus as McProcessingStatus;
  }

  if (typeof minFinalScore === 'number') {
    queryParams.minFinalScore = minFinalScore;
  }

  if (minReferenceDate) {
    queryParams.minReferenceDate = minReferenceDate;
  }

  if (typeof withPlaceholders === 'boolean') {
    queryParams.withPlaceholders = withPlaceholders;
  }

  if (includeStatus) {
    if (statuses) {
      queryParams.statuses = statuses;
    } else if (typeof status === 'number') {
      queryParams.status = status;
    }
  }

  if (includePagination) {
    if (typeof page === 'number') queryParams.page = page;
    if (typeof perPage === 'number') queryParams.perPage = perPage;
    if (typeof offset === 'number') queryParams.offset = offset;
  }

  return queryParams;
};
